// Network graph explorer (PLAN §9, §12.7 screen 7; task 24): the subgraph the API returns, the filters it
// understands, the saved-view state, and the pure helpers both sides share (period overlap, label direction).
// Heavy analytics and layout run client-side on the fetched subgraph (PLAN §3); D1 only answers neighbourhoods and
// shortest paths.

import { type EntitySummary, isReviewStatus, isSensitivity, REVIEW_STATUSES, type ReviewStatus } from './claims';
import type { RelationCategory } from './schema/types';
import type { Sensitivity } from './uploads';

/** Graph expand target: ≤ 500 nodes in < 1 s (PLAN §13.3). The explorer stops adding nodes beyond this. */
export const GRAPH_MAX_NODES = 500;
/** Links returned per expand call; more are reported as `truncated`. */
export const GRAPH_EXPAND_LIMIT = 300;
/** Entities expanded in one call. */
export const GRAPH_MAX_SEEDS = 50;
/** Longest path searched (hops). */
export const GRAPH_MAX_PATH = 6;

/** Statuses a link can be filtered by. Rejected claims never reach the graph (as on profiles). */
export const GRAPH_STATUSES = REVIEW_STATUSES.filter(s => s !== 'rejected') as Exclude<ReviewStatus, 'rejected'>[];

export interface GraphNode extends EntitySummary {
  /** Current, non-rejected links of this entity (both directions), whatever the filter: "5 of 12 shown". */
  degree: number;
}

/** One current, non-rejected relation claim (or table row pointing at an entity) between two entities. */
export interface GraphEdge {
  /** The claim id. */
  id: string;
  source: string;
  target: string;
  predicate: string;
  /** Relation label from the subject's side ("employs"); the inverse is `inverseLabel`. */
  label: string;
  inverseLabel: string | null;
  category: RelationCategory | 'row' | null;
  directed: boolean;
  status: ReviewStatus;
  sensitivity: Sensitivity;
  validFrom: string | null;
  validTo: string | null;
  periodText: string | null;
  amount: number | null;
  currency: string | null;
}

export interface GraphData {
  nodes: GraphNode[];
  edges: GraphEdge[];
  /** More links matched than were returned (expand fewer entities, or narrow the filter). */
  truncated: boolean;
}

export interface GraphPath extends GraphData {
  found: boolean;
  /** Entity ids from `from` to `to`, in order (empty when not found). */
  path: string[];
  /** Hops searched when nothing was found. */
  searched: number;
}

/** Link filters (PLAN §9: relation type / period / status / sensitivity). Everything optional = no filter. */
export interface GraphFilter {
  /** Relation type keys (table rows use their field key). */
  predicates?: string[];
  statuses?: ReviewStatus[];
  /** Only links (and entities) of this sensitivity. */
  sensitivity?: Sensitivity;
  /** Period overlap, ISO prefixes (YYYY, YYYY-MM, YYYY-MM-DD). */
  from?: string;
  to?: string;
  /** Links without dates stay visible under a period filter unless this is false. */
  undated?: boolean;
  /** Leave out off-record links (pictures made for a report: off-record claims are never exported). */
  onRecord?: boolean;
}

const ISO_PREFIX = /^\d{4}(-\d{2}(-\d{2})?)?$/;
const PREDICATE = /^[a-z][a-z0-9_]{0,47}$/;

/** Parses the filter from query-string values (comma lists), dropping anything malformed. */
export function parseGraphFilter(q: Record<string, string | undefined>): GraphFilter {
  const list = (v?: string) =>
    (v ?? '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  const f: GraphFilter = {};
  const predicates = list(q.predicates).filter(p => PREDICATE.test(p));
  if (predicates.length) f.predicates = predicates.slice(0, 60);
  const statuses = list(q.statuses).filter(isReviewStatus);
  if (statuses.length) f.statuses = statuses;
  if (isSensitivity(q.sensitivity)) f.sensitivity = q.sensitivity;
  if (q.from && ISO_PREFIX.test(q.from)) f.from = q.from;
  if (q.to && ISO_PREFIX.test(q.to)) f.to = q.to;
  if (q.undated === '0') f.undated = false;
  if (q.onRecord === '1' || q.onRecord === 'true') f.onRecord = true;
  return f;
}

/** The filter as query-string pairs (the inverse of `parseGraphFilter`). */
export function graphFilterParams(f: GraphFilter): Record<string, string> {
  const out: Record<string, string> = {};
  if (f.predicates?.length) out.predicates = f.predicates.join(',');
  if (f.statuses?.length) out.statuses = f.statuses.join(',');
  if (f.sensitivity) out.sensitivity = f.sensitivity;
  if (f.from) out.from = f.from;
  if (f.to) out.to = f.to;
  if (f.undated === false) out.undated = '0';
  if (f.onRecord) out.onRecord = '1';
  return out;
}

/**
 * Does a link's period overlap [from, to]? Dates are ISO prefixes of any precision, compared so that a coarser date
 * covers its whole span ("2020" overlaps "2020-06"). Undated links count as overlapping unless `undated` is false.
 */
export function periodOverlaps(
  e: Pick<GraphEdge, 'validFrom' | 'validTo'>,
  from?: string | null,
  to?: string | null,
  undated = true,
): boolean {
  if (!from && !to) return true;
  if (!e.validFrom && !e.validTo) return undated;
  // "~" sorts after every digit and "-": "2020~" is the end of 2020, whatever the other side's precision.
  if (from && e.validTo && `${e.validTo}~` < from) return false;
  if (to && e.validFrom && e.validFrom > `${to}~`) return false;
  return true;
}

/** Does the edge pass the filter (client side, for links already on screen)? */
export function edgeMatches(e: GraphEdge, f: GraphFilter): boolean {
  if (f.predicates?.length && !f.predicates.includes(e.predicate)) return false;
  if (f.statuses?.length && !f.statuses.includes(e.status)) return false;
  if (f.sensitivity && e.sensitivity !== f.sensitivity) return false;
  return periodOverlaps(e, f.from, f.to, f.undated !== false);
}

/** Years spanned by the links (for the time slider), or null when none is dated. */
export function edgeYears(edges: Pick<GraphEdge, 'validFrom' | 'validTo'>[]): { min: number; max: number } | null {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const e of edges)
    for (const d of [e.validFrom, e.validTo]) {
      const y = d ? Number(d.slice(0, 4)) : Number.NaN;
      if (Number.isFinite(y)) {
        min = Math.min(min, y);
        max = Math.max(max, y);
      }
    }
  return Number.isFinite(min) ? { min, max } : null;
}

// ─── Labels in any script (task 24 AC: mixed Arabic/Latin labels render correctly) ─────────────────────────────

// Strong right-to-left characters: Hebrew, Arabic, Syriac, Thaana, NKo, Samaritan, Mandaic, Arabic supplements and
// presentation forms. Strong left-to-right: Latin, Greek, Cyrillic, and other letters outside those blocks.
const RTL = /[֐-ࣿיִ-﷿ﹰ-﻿]|\uD802[\uDC00-\uDFFF]|\uD803[\uDC00-\uDEFF]|\uD83A[\uDC00-\uDFFF]|\uD83B[\uDC00-\uDEFF]/;
const LTR = /[A-Za-zÀ-ɏͰ-ϿЀ-ԯḀ-῿Ⰰ-ⷿ぀-鿿가-힯]/;

/**
 * Base direction of a label as `dir="auto"` computes it (Unicode bidi rules P2–P3): the first strong character
 * decides. "حسن حداد (Hassan Haddad)" is right-to-left, "Hassan Haddad (حسن حداد)" left-to-right; neutral-only text
 * (numbers, punctuation) is left-to-right.
 */
export function textDirection(text: string): 'ltr' | 'rtl' {
  for (const ch of text) {
    if (RTL.test(ch)) return 'rtl';
    if (LTR.test(ch)) return 'ltr';
  }
  return 'ltr';
}

/** Does the text contain right-to-left script (so the Arabic font must be loaded before drawing it)? */
export const hasRtl = (text: string): boolean => RTL.test(text);

/**
 * Shortens a label to `max` characters without splitting a character (surrogate pairs, combining marks such as
 * Arabic harakat stay with their base letter). The ellipsis goes at the logical end, which is the visual left of a
 * right-to-left label.
 */
export function truncateLabel(text: string, max = 32): string {
  const clusters =
    typeof Intl !== 'undefined' && 'Segmenter' in Intl
      ? [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text)].map(s => s.segment)
      : [...text];
  if (clusters.length <= max) return text;
  return `${clusters
    .slice(0, max - 1)
    .join('')
    .trimEnd()}…`;
}

// ─── Saved views ──────────────────────────────────────────────────────────────

export const SAVED_VIEW_KINDS = ['graph'] as const;
export type SavedViewKind = (typeof SAVED_VIEW_KINDS)[number];

/** What a saved graph view restores: the entities on screen and where, pins, hidden ones, filters, time, path. */
export interface GraphViewState {
  version: 1;
  nodes: { id: string; x: number; y: number; pinned?: boolean }[];
  hidden: string[];
  filter: GraphFilter;
  /** Time slider position (a year), or null when off. */
  asOf: number | null;
  path: { from: string; to: string } | null;
  /** Entities whose neighbours were loaded (so a restored view can still expand the rest). */
  expanded: string[];
}

export interface SavedView {
  id: string;
  kind: SavedViewKind;
  name: string;
  state: GraphViewState;
  /** Case the view is saved to (Cases arrive later; null = personal). */
  caseId: string | null;
  createdBy: string | null;
  createdAt: number;
  updatedAt: number;
}

export const SAVED_VIEW_NAME_MAX = 120;

/** Validates a saved graph view's state; returns the cleaned state or the first problem. */
export function parseGraphViewState(v: unknown): { ok: true; state: GraphViewState } | { ok: false; error: string } {
  if (!v || typeof v !== 'object') return { ok: false, error: 'The view is empty.' };
  const s = v as Record<string, unknown>;
  const ids = (x: unknown) => (Array.isArray(x) ? x.filter((i): i is string => typeof i === 'string' && i.length <= 40) : []);
  const rawNodes = Array.isArray(s.nodes) ? s.nodes : [];
  if (!rawNodes.length) return { ok: false, error: 'Add at least one entity to the graph before saving the view.' };
  if (rawNodes.length > GRAPH_MAX_NODES) return { ok: false, error: `A view holds at most ${GRAPH_MAX_NODES} entities.` };
  const nodes: GraphViewState['nodes'] = [];
  for (const n of rawNodes) {
    const o = n as Record<string, unknown>;
    if (typeof o?.id !== 'string' || o.id.length > 40) return { ok: false, error: 'The view lists an entity without an id.' };
    const x = Number(o.x);
    const y = Number(o.y);
    nodes.push({ id: o.id, x: Number.isFinite(x) ? x : 0, y: Number.isFinite(y) ? y : 0, ...(o.pinned ? { pinned: true } : {}) });
  }
  const f = (s.filter ?? {}) as Record<string, unknown>;
  const filter = parseGraphFilter({
    predicates: Array.isArray(f.predicates) ? f.predicates.join(',') : undefined,
    statuses: Array.isArray(f.statuses) ? f.statuses.join(',') : undefined,
    sensitivity: typeof f.sensitivity === 'string' ? f.sensitivity : undefined,
    from: typeof f.from === 'string' ? f.from : undefined,
    to: typeof f.to === 'string' ? f.to : undefined,
    undated: f.undated === false ? '0' : undefined,
  });
  const asOf = typeof s.asOf === 'number' && Number.isInteger(s.asOf) && s.asOf > 0 && s.asOf < 10000 ? s.asOf : null;
  const p = s.path as Record<string, unknown> | null | undefined;
  const path = p && typeof p.from === 'string' && typeof p.to === 'string' ? { from: p.from, to: p.to } : null;
  return { ok: true, state: { version: 1, nodes, hidden: ids(s.hidden), filter, asOf, path, expanded: ids(s.expanded) } };
}

export const isGraphStatus = (s: unknown): s is (typeof GRAPH_STATUSES)[number] => isReviewStatus(s) && s !== 'rejected';
